import "./style.css";
import GenericTable from "../../../Components/GenericTable";
import TotalEvents from "../../../Assests/icons/Dashboard Tabs Icons/TotalEvents";
import CompletedEvents from "../../../Assests/icons/Dashboard Tabs Icons/CompletedEvents";
import TodayEvents from "../../../Assests/icons/Dashboard Tabs Icons/TodayEvents";
import TotalRevenue from "../../../Assests/icons/Dashboard Tabs Icons/TotalRevenue";
import ThisMonth from "../../../Assests/icons/Dashboard Tabs Icons/ThisMonth";
import HighestRevenue from "../../../Assests/icons/Dashboard Tabs Icons/HighestRevenue";
import AvgRevenue from "../../../Assests/icons/Dashboard Tabs Icons/AvgRevenue";
import UpcomingEvents from "../../../Assests/icons/Dashboard Tabs Icons/UpcomingEvents";
import TimelineGraph from "./TimeLineGraph";
import { Input, Button, Space, message, Select, DatePicker, Tag } from "antd";
import {
  SearchOutlined,
  MailOutlined,
  PhoneOutlined,
  CalendarOutlined,
} from "@ant-design/icons";
import { useRef, useState, useEffect } from "react";
import { dashboardAPI, eventAPI } from "../../../utils/api";
import { useNavigate } from "react-router-dom";
import { ClipLoader } from "react-spinners";
import dayjs from "dayjs";

const { RangePicker } = DatePicker;

const Overview = () => {
  const [stats, setStats] = useState({
    total_events: 0,
    completed_events: 0,
    today_events: 0,
    upcoming_events: 0,
    total_revenue: 0,
    this_month_revenue: 0,
    highest_revenue: 0,
    average_revenue: 0,
    period_label: "",
    expected_revenue: 0,
    actual_collected: 0,
    outstanding_balance: 0,
  });
  const [recentEvents, setRecentEvents] = useState([]);
  const [upcomingEvents, setUpcomingEvents] = useState([]);
  const [timeline, setTimeline] = useState([]);
  const [loading, setLoading] = useState(false);
  const [messageApi, contextHolder] = message.useMessage();
  const searchInput = useRef(null);
  const navigate = useNavigate();

  // Earnings-by-period controls
  const [periodMode, setPeriodMode] = useState("this_month");
  const [selectedYear, setSelectedYear] = useState(dayjs());
  const [customRange, setCustomRange] = useState(null);

  // The server's own clock — a viewer's device can be a full calendar day
  // off from the app's timezone, so "today" is whatever the server says it
  // is, not whatever dayjs() reads off the local machine.
  const [serverToday, setServerToday] = useState(dayjs());

  // Date lookup ("what's happening on this date")
  const [selectedDate, setSelectedDate] = useState(dayjs());
  const [dayEvents, setDayEvents] = useState([]);
  const [dayEventsLoading, setDayEventsLoading] = useState(false);

  const loadPeriodStats = async (mode, yearVal, rangeVal) => {
    try {
      let params;
      if (mode === "year") {
        const y = yearVal || serverToday;
        params = {
          period: "custom",
          start: y.startOf("year").format("YYYY-MM-DD"),
          end: y.endOf("year").format("YYYY-MM-DD"),
        };
      } else if (mode === "custom") {
        if (!rangeVal || rangeVal.length !== 2) return;
        params = {
          period: "custom",
          start: rangeVal[0].format("YYYY-MM-DD"),
          end: rangeVal[1].format("YYYY-MM-DD"),
        };
      } else {
        params = { period: mode };
      }
      const response = await dashboardAPI.getStats(params);
      setStats(response.data);
    } catch (error) {
      messageApi.error("Failed to load earnings for the selected period");
    }
  };

  const loadDayEvents = async (date) => {
    try {
      setDayEventsLoading(true);
      const response = await dashboardAPI.getDayEvents(date.format("YYYY-MM-DD"));
      setDayEvents(response.data || []);
    } catch (error) {
      messageApi.error("Failed to load events for that date");
    } finally {
      setDayEventsLoading(false);
    }
  };

  // Load dashboard data on mount
  useEffect(() => {
    const loadDashboardData = async () => {
      try {
        setLoading(true);

        // Ask the server what day it is before anything date-sensitive
        // defaults to the viewer's own (possibly wrong) device clock.
        let today = dayjs();
        try {
          const serverDateResponse = await dashboardAPI.getServerDate();
          today = dayjs(serverDateResponse.today);
        } catch (dateError) {
          console.error("Failed to fetch server date, falling back to local clock:", dateError);
        }
        setServerToday(today);
        setSelectedDate(today);
        setSelectedYear(today);

        // Fetch stats
        const statsResponse = await dashboardAPI.getStats();
        setStats(statsResponse.data);

        // Fetch recent + upcoming events
        const eventsResponse = await dashboardAPI.getRecentEvents(10);
        setRecentEvents(eventsResponse.data);

        const upcomingResponse = await dashboardAPI.getUpcomingEvents(5);
        setUpcomingEvents(upcomingResponse.data);

        const timelineResponse = await dashboardAPI.getTimeline();
        setTimeline(timelineResponse.data);

        await loadDayEvents(today);
      } catch (error) {
        messageApi.error("Failed to load dashboard data");
        console.error("Dashboard error:", error);
      } finally {
        setLoading(false);
      }
    };

    loadDashboardData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [messageApi]);

  const handleSearch = (confirm) => {
    confirm();
  };

  const handleReset = (clearFilters, confirm) => {
    clearFilters();
    confirm();
  };

  const getColumnSearchProps = (dataIndex) => ({
    filterDropdown: ({
      setSelectedKeys,
      selectedKeys,
      confirm,
      clearFilters,
    }) => (
      <div style={{ padding: 8 }}>
        <Input
          ref={searchInput}
          placeholder={`Search ${dataIndex}`}
          value={selectedKeys[0]}
          onChange={(e) =>
            setSelectedKeys(e.target.value ? [e.target.value] : [])
          }
          onPressEnter={() => handleSearch(confirm)}
          style={{ marginBottom: 8, display: "block" }}
        />
        <Space>
          <Button
            type="primary"
            onClick={() => handleSearch(confirm)}
            size="small"
            style={{ width: 90 }}
          >
            Search
          </Button>
          <Button
            onClick={() => handleReset(clearFilters, confirm)}
            size="small"
            style={{ width: 90 }}
          >
            Reset
          </Button>
        </Space>
      </div>
    ),
    filterIcon: (filtered) => (
      <SearchOutlined style={{ color: filtered ? "#1890ff" : undefined }} />
    ),
    onFilter: (value, record) =>
      record[dataIndex]
        ? record[dataIndex]
            .toString()
            .toLowerCase()
            .includes(value.toLowerCase())
        : "",
    onFilterDropdownVisibleChange: (visible) => {
      if (visible) {
        setTimeout(() => searchInput.current?.select(), 100);
      }
    },
  });

  const columns = [
    {
      title: "Client Name",
      dataIndex: "clientName",
      key: "clientName",
      enableSearch: true,
    },
    {
      title: "Event Name",
      dataIndex: "eventName",
      key: "eventName",
      enableSearch: true,
    },
    {
      title: "Email",
      dataIndex: "email",
      key: "email",
      enableSearch: true,
      render: (text) => (
        <span style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <MailOutlined style={{ color: "#3A974C" }} />
          {text}
        </span>
      ),
    },
    {
      title: "Phone Number",
      dataIndex: "phone",
      key: "phone",
      enableSearch: true,

      render: (text) => (
        <span style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <PhoneOutlined style={{ color: "#3980F2" }} />
          {text}
        </span>
      ),
    },
    {
      title: "Date",
      dataIndex: "date",
      key: "date",
      sorter: (a, b) => new Date(a.date) - new Date(b.date),
      render: (text) => (
        <span style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <CalendarOutlined style={{ color: "#E2AE03" }} />
          {text}
        </span>
      ),
    },
    {
      title: "Event Value",
      dataIndex: "value",
      key: "value",
      sorter: (a, b) => a.value - b.value,
      render: (value) => `$${value.toLocaleString()}`,
    },
    {
      title: "Event Status",
      dataIndex: "status",
      key: "status",
      filters: [
        { text: "Complete", value: "complete" },
        { text: "Booked", value: "booked" },
        { text: "In Query", value: "in_query" },
        { text: "Cancel", value: "cancel" },
      ],
      onFilter: (value, record) => record.status === value,
      render: (status) => {
        const colorMap = {
          complete: "green",
          booked: "blue",
          in_query: "orange",
          cancel: "red",
        };

        return (
          <span style={{ color: colorMap[status], fontWeight: 500 }}>
            {status.charAt(0).toUpperCase() + status.slice(1).replace("_", " ")}
          </span>
        );
      },
    },
    {
      title: "Payment",
      key: "payment",
      render: (_, record) => (
        <Space direction="vertical" size={4}>
          {!record.deposit_met && record.status !== "cancel" && (
            <Tag color="warning">Deposit Pending</Tag>
          )}
          {record.balance_due_warning && (
            <Tag color="error">Balance Due Soon</Tag>
          )}
        </Space>
      ),
    },
  ];

  return loading ? (
    <div
      style={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
      }}
      className="pdf-loader-overlay"
    >
      <ClipLoader size={60} color="#e2ae03" />
    </div>
  ) : (
    <div className="overview">
      {contextHolder}
      <div className="tabs-container">
        <div className="tab">
          <div className="icon">
            <TotalEvents />
          </div>
          <div className="tab-detail">
            <div className="value">{stats.total_events}</div>
            <div className="title">Total Events</div>
          </div>
        </div>

        <div className="tab">
          <div className="icon">
            <CompletedEvents />
          </div>
          <div className="tab-detail">
            <div className="value">{stats.completed_events}</div>
            <div className="title">Completed Events</div>
          </div>
        </div>

        <div className="tab">
          <div className="icon">
            <TodayEvents />
          </div>
          <div className="tab-detail">
            <div className="value">{stats.today_events}</div>
            <div className="title">Today Events</div>
          </div>
        </div>

        <div className="tab">
          <div className="icon">
            <TotalRevenue />
          </div>
          <div className="tab-detail">
            <div className="value">
              ${(stats.total_revenue / 1000).toFixed(1)}K
            </div>
            <div className="title">Total Revenue</div>
          </div>
        </div>

        <div className="tab">
          <div className="icon">
            <ThisMonth />
          </div>
          <div className="tab-detail">
            <div className="value">
              ${(stats.this_month_revenue / 1000).toFixed(1)}K
            </div>
            <div className="title">This Month</div>
          </div>
        </div>

        <div className="tab">
          <div className="icon">
            <HighestRevenue />
          </div>
          <div className="tab-detail">
            <div className="value">
              ${(stats.highest_revenue / 1000).toFixed(1)}K
            </div>
            <div className="title">Highest Revenue</div>
          </div>
        </div>

        <div className="tab">
          <div className="icon">
            <AvgRevenue />
          </div>
          <div className="tab-detail">
            <div className="value">
              ${(stats.average_revenue / 1000).toFixed(1)}K
            </div>
            <div className="title">Avg Revenue</div>
          </div>
        </div>

        <div className="tab">
          <div className="icon">
            <UpcomingEvents />
          </div>
          <div className="tab-detail">
            <div className="value">{stats.upcoming_events}</div>
            <div className="title">Upcoming Events</div>
          </div>
        </div>
      </div>

      <div className="earnings-by-period" style={{ margin: "20px 0" }}>
        <div
          className="title"
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            flexWrap: "wrap",
            gap: 12,
            marginBottom: 12,
          }}
        >
          <span>Earnings — {stats.period_label || "This Month"}</span>
          <Space wrap>
            <Select
              value={periodMode}
              style={{ width: 160 }}
              onChange={(value) => {
                setPeriodMode(value);
                if (value === "year") loadPeriodStats("year", selectedYear);
                else if (value === "custom") loadPeriodStats("custom", null, customRange);
                else loadPeriodStats(value);
              }}
              options={[
                { value: "this_month", label: "This Month" },
                { value: "this_quarter", label: "This Quarter" },
                { value: "this_year", label: "This Year" },
                { value: "year", label: "Pick a Year" },
                { value: "custom", label: "Custom Range" },
                { value: "all_time", label: "All Time" },
              ]}
            />
            {periodMode === "year" && (
              <DatePicker
                picker="year"
                value={selectedYear}
                onChange={(value) => {
                  if (!value) return;
                  setSelectedYear(value);
                  loadPeriodStats("year", value);
                }}
              />
            )}
            {periodMode === "custom" && (
              <RangePicker
                value={customRange}
                onChange={(value) => {
                  setCustomRange(value);
                  if (value && value.length === 2) {
                    loadPeriodStats("custom", null, value);
                  } else {
                    // Range was cleared — fall back to This Month rather
                    // than leaving the previous period's figures on screen.
                    setPeriodMode("this_month");
                    loadPeriodStats("this_month");
                  }
                }}
              />
            )}
          </Space>
        </div>

        <div className="tabs-container">
          <div className="tab">
            <div className="tab-detail">
              <div className="value">
                ${Number(stats.expected_revenue || 0).toLocaleString()}
              </div>
              <div className="title">Expected</div>
            </div>
          </div>
          <div className="tab">
            <div className="tab-detail">
              <div className="value">
                ${Number(stats.actual_collected || 0).toLocaleString()}
              </div>
              <div className="title">Actual Collected</div>
            </div>
          </div>
          <div className="tab">
            <div className="tab-detail">
              <div className="value">
                ${Number(stats.outstanding_balance || 0).toLocaleString()}
              </div>
              <div className="title">Outstanding</div>
            </div>
          </div>
        </div>
      </div>

      <div className="graph-container">
        <div className="title">Today Events</div>
        <TimelineGraph filteredData={timeline} />
      </div>

      <div className="day-lookup-section" style={{ margin: "20px 0" }}>
        <div
          className="title"
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            flexWrap: "wrap",
            gap: 12,
            marginBottom: 12,
          }}
        >
          <span>What's happening on {selectedDate.format("MMMM D, YYYY")}</span>
          <DatePicker
            value={selectedDate}
            onChange={(value) => {
              const date = value || serverToday;
              setSelectedDate(date);
              loadDayEvents(date);
            }}
          />
        </div>

        <GenericTable
          title=""
          loading={dayEventsLoading}
          onRowClick={(record) => {
            navigate("/view-event-detail", { state: { eventId: record.id } });
          }}
          dataSource={dayEvents.map((event, index) => ({
            ...event,
            key: event.id || index,
          }))}
          columns={[
            { title: "Time", dataIndex: "time", key: "time" },
            { title: "Event", dataIndex: "eventName", key: "eventName" },
            { title: "Client", dataIndex: "clientName", key: "clientName" },
            { title: "Venue", dataIndex: "venue", key: "venue" },
            { title: "Package", dataIndex: "package", key: "package" },
            {
              title: "Add-Ons",
              dataIndex: "addOns",
              key: "addOns",
              render: (addOns) => (addOns || []).join(", "),
            },
            {
              title: "Value",
              dataIndex: "value",
              key: "value",
              render: (value) => `$${Number(value).toLocaleString()}`,
            },
            { title: "Status", dataIndex: "status", key: "status" },
          ]}
        />
      </div>

      <div className="upcoming-events-section" style={{ margin: "20px 0" }}>
        <GenericTable
          title="Upcoming Events"
          columns={columns}
          dataSource={upcomingEvents.map((event, index) => ({
            ...event,
            key: event.id || index,
          }))}
          onRowClick={(record) => {
            navigate("/view-event-detail", { state: { eventId: record.id } });
          }}
          loading={loading}
        />
      </div>

      <div className="recent-events-section">
        <GenericTable
          title="Recent Events"
          columns={columns}
          dataSource={recentEvents.map((event, index) => ({
            ...event,
            key: event.id || index,
          }))}
          onRowClick={(record) => {
            navigate("/view-event-detail", { state: { eventId: record.id } });
          }}
          loading={loading}
        />
      </div>
    </div>
  );
};

export default Overview;
